/**
 * The two funding questions every path that spends a credit has to agree on
 * (LG-1004): can this Sprint still take a credit at all, and does this
 * client's balance fund this week.
 *
 * The lock sweep (LG-1001) and the admin late add (LG-1004) both debit a
 * client, and before this module each carried its own copy of the answer --
 * an inline `isFree || balance > 0` and an inline `status = 'scheduled' AND
 * endsAt > now`. One copy per caller is how a free week ends up charged by
 * one path and not the other. Both callers ask here now, and the SQL twin of
 * the Sprint-level rule lives beside the sweep's read, and
 * `sprint-funding-rules.test.ts` pins that the read renders the same two
 * clauses this module checks.
 *
 * Pure on purpose, in the `participation-transition.ts` idiom: no db import,
 * no I/O, structurally typed parameters. The admin page's consequence
 * preview imports it too, so the promise it makes before a move and the
 * service's answer after it come from one rule.
 */
import type { SprintStatus } from '../../db/schema/enums.js'

/**
 * Whether a Sprint in this status can take a credit from anyone.
 *
 * A total `Record` rather than a `!== 'scheduled'` test, matching
 * `CLIENT_VISIBLE_BY_WEEK_STATUS` in `week-visibility.ts`: a third
 * `sprintStatusEnum` member has to be classified here, as a compile error,
 * rather than inheriting the answer.
 */
const CHARGES_BY_SPRINT_STATUS = {
  scheduled: true,
  suspended: false,
} as const satisfies Record<SprintStatus, boolean>

export function sprintStatusCanCharge(status: SprintStatus): boolean {
  return CHARGES_BY_SPRINT_STATUS[status]
}

/**
 * Whether a Sprint can still take a credit: its status allows it AND it has
 * not already ended.
 *
 * A Sprint that ends while a participation is still `upcoming` was stranded
 * by a missed sweep (LG-999): no automated path will ever debit it, and
 * `canTransition` refuses every actor after `endsAt`, so nothing may count on
 * it being paid for. Suspension is the other half -- a suspended Sprint
 * charges nobody -- and is the hook LG-1078 extends when Suspend / Mark free
 * are built.
 */
export function sprintCanStillCharge(
  sprint: { status: SprintStatus; endsAt: Date },
  now: Date,
): boolean {
  return (
    sprintStatusCanCharge(sprint.status) &&
    now.getTime() < sprint.endsAt.getTime()
  )
}

/**
 * Whether a client's balance funds one week of this Sprint.
 *
 * A free Sprint locks everyone in regardless of balance and still writes its
 * ledger row, at `delta 0` -- the row is the receipt that this participation
 * was settled, and it holds the idempotency latch. Everyone else needs a
 * credit to spend.
 */
export function weekIsFundable(
  sprint: { isFree: boolean },
  balance: number,
): boolean {
  return sprint.isFree || balance > 0
}

/** What one lock-in writes to the ledger for this Sprint. */
export function lockInDelta(sprint: { isFree: boolean }): 0 | -1 {
  return sprint.isFree ? 0 : -1
}
